import React from "react";
import { Box, ButtonBase, Typography } from "@mui/material";
import Icon from "@mui/material/Icon";

import {
  ALL_LEGEND_SLOTS,
  LEGEND_PRIORITY,
  LEGEND_SLOT_STYLES,
  LEGEND_WIDTH,
  useLegendSlot,
} from "./legendSlots";
import {
  RISK_FROM_ACCENT,
  RISK_FROM_CLASS_COUNT,
  RISK_FROM_POINT_RADIUS,
  RISK_FROM_RAMP,
  RiskFromMetric,
  RiskFromMetricDef,
  riskFromMetric,
} from "./riskFromStyles";
import type { UnitSystem } from "../../utils/units";

/**
 * Which body the card shows above the ramp:
 * - grid: a histogram of ignitions per class; each bar toggles its class.
 * - points: the size key (disc diameter by class).
 * - single: the one ignition's value as a marker on the ramp.
 */
export type RiskFromLegendVariant = "grid" | "points" | "single";

export interface RiskFromLegendProps {
  variant: RiskFromLegendVariant;
  metric: RiskFromMetric;
  onMetricChange: (metric: RiskFromMetric) => void;
  unitSystem: UnitSystem;
  /** Ignitions per class for the active metric (length 5). */
  counts: number[];
  hiddenClasses: number[];
  onToggleClass: (classIndex: number) => void;
  onShowAll: () => void;
  /** Loaded ignition's value and ramp position (0–100), if one is loaded. */
  selected: { value: string; position: number } | null;
  /** Hovered ignition's ramp position (0–100), if the pointer is over one. */
  hoverPosition: number | null;
  /** "Mean of 12 scenarios" for ensembles; nothing for single runs. */
  note?: string | null;
}

const HIDDEN_OPACITY = 0.22;

const RiskFromLegend: React.FC<RiskFromLegendProps> = (props) => {
  const slot = useLegendSlot("risk-from", ALL_LEGEND_SLOTS, LEGEND_PRIORITY.riskFrom);
  const def = riskFromMetric(props.metric, props.unitSystem);

  return (
    <Box
      sx={{
        position: "absolute",
        ...LEGEND_SLOT_STYLES[slot],
        width: LEGEND_WIDTH,
        boxSizing: "border-box",
        backgroundColor: "rgba(30, 41, 59, 0.95)",
        backdropFilter: "blur(8px)",
        border: "1px solid rgba(148, 163, 184, 0.25)",
        borderRadius: "10px",
        padding: "10px 12px",
        zIndex: 1000,
        color: "#f8fafc",
        fontSize: "12px",
        display: "flex",
        flexDirection: "column",
        gap: 1.25,
        fontVariantNumeric: "tabular-nums",
      }}
    >
      <Box>
        <Typography sx={{ fontWeight: 600, fontSize: "0.8rem", lineHeight: 1.2 }}>
          Risk from ignitions
        </Typography>
        {props.note && (
          <Typography sx={{ fontSize: "0.62rem", color: "#94a3b8", mt: 0.25 }}>
            {props.note}
          </Typography>
        )}
      </Box>

      <MetricSwitch metric={props.metric} unitSystem={props.unitSystem} onChange={props.onMetricChange} />

      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
        {props.variant === "grid" ? (
          <Histogram
            def={def}
            counts={props.counts}
            hiddenClasses={props.hiddenClasses}
            onToggleClass={props.onToggleClass}
          />
        ) : props.variant === "points" ? (
          <SizeKey />
        ) : (
          <ValueMarker selected={props.selected} />
        )}

        <Ramp
          hiddenClasses={props.hiddenClasses}
          selectedPosition={props.selected?.position ?? null}
          hoverPosition={props.hoverPosition}
        />

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: `repeat(${RISK_FROM_CLASS_COUNT}, minmax(0, 1fr))`,
            gap: "3px",
          }}
        >
          {def.ticks.map((tick, i) => (
            <Typography key={i} sx={{ fontSize: "0.62rem", color: "#94a3b8", lineHeight: 1 }}>
              {tick}
            </Typography>
          ))}
        </Box>
      </Box>

      {props.hiddenClasses.length > 0 && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pt: 1,
            borderTop: "1px solid rgba(255,255,255,0.07)",
            fontSize: "0.72rem",
            color: "#cbd5e1",
          }}
        >
          <span>
            {props.hiddenClasses.length === 1
              ? "1 class hidden"
              : `${props.hiddenClasses.length} classes hidden`}
          </span>
          <ButtonBase
            onClick={props.onShowAll}
            sx={{
              color: RISK_FROM_ACCENT,
              fontSize: "0.72rem",
              fontWeight: 500,
              fontFamily: "inherit",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            Show all
          </ButtonBase>
        </Box>
      )}
    </Box>
  );
};

const MetricSwitch: React.FC<{
  metric: RiskFromMetric;
  unitSystem: UnitSystem;
  onChange: (metric: RiskFromMetric) => void;
}> = ({ metric, unitSystem, onChange }) => {
  const options: RiskFromMetric[] = ["fire_size", "impacted_assets"];
  return (
    <Box
      role="group"
      aria-label="Colour ignitions by"
      sx={{
        display: "flex",
        gap: "2px",
        p: "3px",
        borderRadius: "9px",
        backgroundColor: "rgba(255,255,255,0.05)",
      }}
    >
      {options.map((key) => {
        const def = riskFromMetric(key, unitSystem);
        const on = key === metric;
        return (
          <ButtonBase
            key={key}
            aria-pressed={on}
            onClick={() => onChange(key)}
            sx={{
              flex: "1 1 0",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 0.6,
              height: 28,
              borderRadius: "6px",
              fontFamily: "inherit",
              fontSize: "0.72rem",
              fontWeight: 500,
              color: on ? RISK_FROM_ACCENT : "#cbd5e1",
              backgroundColor: on ? "rgba(96, 209, 175, 0.16)" : "transparent",
              boxShadow: on ? "inset 0 0 0 1px rgba(96, 209, 175, 0.35)" : "none",
              transition: "background-color .15s, color .15s",
              "&:hover": { backgroundColor: on ? "rgba(96, 209, 175, 0.2)" : "rgba(255,255,255,0.06)" },
            }}
          >
            <Icon sx={{ fontSize: 15 }}>{def.icon}</Icon>
            <span>{def.label}</span>
          </ButtonBase>
        );
      })}
    </Box>
  );
};

const Histogram: React.FC<{
  def: RiskFromMetricDef;
  counts: number[];
  hiddenClasses: number[];
  onToggleClass: (classIndex: number) => void;
}> = ({ def, counts, hiddenClasses, onToggleClass }) => {
  const max = Math.max(1, ...counts);
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: `repeat(${RISK_FROM_CLASS_COUNT}, minmax(0, 1fr))`,
        gap: "3px",
        alignItems: "end",
      }}
    >
      {counts.map((count, k) => {
        const off = hiddenClasses.includes(k);
        const range = k < RISK_FROM_CLASS_COUNT - 1 ? `${def.ticks[k]}–${def.ticks[k + 1]}` : def.ticks[k];
        return (
          <ButtonBase
            key={k}
            aria-pressed={!off}
            aria-label={`${off ? "Show" : "Hide"} ${range} (${count} ignitions)`}
            onClick={() => onToggleClass(k)}
            sx={{
              height: 50,
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-end",
              alignItems: "stretch",
              gap: "3px",
              borderRadius: "4px",
              "&:hover .bar-fill": { filter: "brightness(1.15)" },
            }}
          >
            <Typography
              component="span"
              sx={{
                fontSize: "0.68rem",
                lineHeight: 1,
                textAlign: "center",
                color: off ? "#64748b" : "#cbd5e1",
                textDecoration: off ? "line-through" : "none",
              }}
            >
              {count}
            </Typography>
            <Box
              className="bar-fill"
              sx={{
                height: Math.round(4 + 30 * (count / max)),
                backgroundColor: RISK_FROM_RAMP[k],
                opacity: off ? HIDDEN_OPACITY : 1,
                borderRadius: "3px 3px 1px 1px",
                transition: "height .25s ease-out, opacity .15s",
              }}
            />
          </ButtonBase>
        );
      })}
    </Box>
  );
};

const SizeKey: React.FC = () => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: `repeat(${RISK_FROM_CLASS_COUNT}, minmax(0, 1fr))`,
      gap: "3px",
      alignItems: "center",
      height: 34,
    }}
  >
    {RISK_FROM_RAMP.map((color, k) => {
      const d = RISK_FROM_POINT_RADIUS[k] * 2;
      return (
        <Box
          key={k}
          sx={{
            justifySelf: "center",
            boxSizing: "border-box",
            width: d,
            height: d,
            borderRadius: "50%",
            backgroundColor: color,
            border: "2px solid #ffffff",
            boxShadow: "0 0 0 1px rgba(10, 14, 22, 0.55)",
          }}
        />
      );
    })}
  </Box>
);

const ValueMarker: React.FC<{ selected: { value: string; position: number } | null }> = ({ selected }) => (
  <Box sx={{ position: "relative", height: 22 }}>
    {selected && (
      <Box
        component="span"
        sx={{
          position: "absolute",
          left: `${selected.position}%`,
          top: 0,
          transform: `translateX(${markerShift(selected.position)})`,
          px: 0.75,
          py: 0.25,
          borderRadius: "4px",
          backgroundColor: RISK_FROM_ACCENT,
          color: "#0B1F19",
          fontSize: "0.68rem",
          fontWeight: 600,
          whiteSpace: "nowrap",
          lineHeight: 1.3,
        }}
      >
        {selected.value}
      </Box>
    )}
  </Box>
);

/** Keep an edge-of-ramp chip inside the card rather than centred off it. */
const markerShift = (position: number) =>
  position < 12 ? "0%" : position > 88 ? "-100%" : "-50%";

const Ramp: React.FC<{
  hiddenClasses: number[];
  selectedPosition: number | null;
  hoverPosition: number | null;
}> = ({ hiddenClasses, selectedPosition, hoverPosition }) => (
  <Box sx={{ position: "relative" }}>
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: `repeat(${RISK_FROM_CLASS_COUNT}, minmax(0, 1fr))`,
        gap: "3px",
      }}
    >
      {RISK_FROM_RAMP.map((color, k) => (
        <Box
          key={k}
          sx={{
            height: 6,
            borderRadius: "2px",
            backgroundColor: color,
            opacity: hiddenClasses.includes(k) ? HIDDEN_OPACITY : 1,
          }}
        />
      ))}
    </Box>
    {selectedPosition !== null && <Tick position={selectedPosition} color={RISK_FROM_ACCENT} />}
    {hoverPosition !== null && hoverPosition !== selectedPosition && (
      <Tick position={hoverPosition} color="#ffffff" />
    )}
  </Box>
);

const Tick: React.FC<{ position: number; color: string }> = ({ position, color }) => (
  <Box
    component="span"
    sx={{
      position: "absolute",
      left: `${position}%`,
      top: -4,
      width: 4,
      height: 14,
      ml: "-2px",
      borderRadius: "2px",
      backgroundColor: color,
      boxShadow: "0 0 0 1.5px #111621",
      pointerEvents: "none",
    }}
  />
);

export default RiskFromLegend;
